import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { finalize, Observable } from 'rxjs';
import { Store } from '@ngxs/store';
import { ApplicationConfig } from '../config/application.config';
import { Constant } from '../../shared/common/constant';
import { ApplicationActions } from '../../shared/store/application/application.actions';

const MIN_LOADER_DISPLAY_MS = 500;
const LOADER_SHOW_DELAY_MS = 150;
const LOADER_STATE_KEY = '__loader__';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  private requestCount = 0;
  private showTimerId: ReturnType<typeof setTimeout> | null = null;
  private hideTimerId: ReturnType<typeof setTimeout> | null = null;
  private loaderShownAt: number | null = null;

  constructor(private readonly store: Store) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const { url, headers } = request;

    const isApiRequest = url.startsWith(ApplicationConfig.apiUrl);

    if (isApiRequest) {
      if (!headers.has(Constant.HTTP_SILENT_MODE)) {
        this.requestCount += 1;

        if (this.hideTimerId !== null) {
          clearTimeout(this.hideTimerId);
          this.hideTimerId = null;
        }

        if (this.requestCount === 1) {
          if (this.showTimerId !== null) {
            clearTimeout(this.showTimerId);
          }

          if (this.loaderShownAt == null) {
            this.showTimerId = setTimeout(() => {
              this.showTimerId = null;
              if (this.requestCount <= 0 || this.loaderShownAt != null) {
                return;
              }

              this.loaderShownAt = Date.now();
              this.store.dispatch(
                new ApplicationActions.SetLoading(true, LOADER_STATE_KEY)
              );
            }, LOADER_SHOW_DELAY_MS);
          }
        }

        return next.handle(request).pipe(
          finalize(() => {
            this.requestCount -= 1;
            if (this.requestCount === 0) {
              if (this.showTimerId !== null) {
                clearTimeout(this.showTimerId);
                this.showTimerId = null;
                return;
              }

              const elapsed = this.loaderShownAt
                ? Date.now() - this.loaderShownAt
                : 0;
              const remainingDelay = Math.max(
                0,
                MIN_LOADER_DISPLAY_MS - elapsed
              );

              this.hideTimerId = setTimeout(() => {
                this.hideTimerId = null;
                this.loaderShownAt = null;
                this.store.dispatch(
                  new ApplicationActions.SetLoading(false, LOADER_STATE_KEY)
                );
              }, remainingDelay);
            }
          })
        );
      }

      return next.handle(request);
    } else {
      return next.handle(request);
    }
  }
}
